import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Page";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Beaker, BookOpen, BadgeCheck, ScrollText, Plus, FlaskConical, CreditCard } from "lucide-react";
import { toast } from "sonner";

const SUB = ["Trial", "Active Monthly", "Active Annual", "Suspended", "Expired"];

const COMMERCIAL_FILTERS = [
  { key: "all", label: "All" },
  { key: "new_registrations", label: "New registrations" },
  { key: "email_not_verified", label: "Email not verified" },
  { key: "payment_pending", label: "Payment pending" },
  { key: "active", label: "Active subscriptions" },
  { key: "expiring", label: "Expiring subscriptions" },
  { key: "invoice_incomplete", label: "Invoice configuration incomplete" },
];
const CNGN = (v, c = "NGN") => (v == null ? "—" : `${c === "NGN" ? "\u20a6" : ""}${Number(v).toLocaleString()}`);

function Commercial() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const load = (f) => api.get(`/admin/commercial/organizations?filter=${f}`).then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(filter); }, [filter]);
  const open = async (id) => {
    try { const { data } = await api.get(`/admin/commercial/organizations/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="admin-commercial">
      <div className="flex flex-wrap gap-2 mb-4">
        {COMMERCIAL_FILTERS.map((f) => (
          <button key={f.key} data-testid={`commercial-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`text-xs rounded-full border px-3 py-1 ${filter === f.key ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white text-slate-600"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
              <th className="px-4 py-2.5">Organisation</th><th className="px-4 py-2.5">Contact</th>
              <th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Seats</th>
              <th className="px-4 py-2.5">Commercial state</th><th className="px-4 py-2.5">Verified</th>
              <th className="px-4 py-2.5">Payment</th><th className="px-4 py-2.5">Subscription</th>
              <th className="px-4 py-2.5">Value</th><th className="px-4 py-2.5">Expiry</th>
              <th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Onboarding</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-muted-foreground">No organisations match this filter.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`commercial-row-${r.id}`} className="border-b last:border-0 cursor-pointer hover:bg-slate-50" onClick={() => open(r.id)}>
                  <td className="px-4 py-2.5 font-medium">{r.organisation}{r.legacy_migrated && <span className="ml-1.5 text-[10px] rounded bg-slate-100 border text-slate-500 px-1.5 py-0.5">LEGACY</span>}</td>
                  <td className="px-4 py-2.5 text-xs">{r.contact_email}</td>
                  <td className="px-4 py-2.5 text-xs">{r.plan || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.active_named_users}/{r.contracted_named_users}</td>
                  <td className="px-4 py-2.5 text-xs">{r.commercial_state || "—"}</td>
                  <td className="px-4 py-2.5">{r.email_verified ? "✓" : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.legacy_migrated ? "—" : r.payment_status}</td>
                  <td className="px-4 py-2.5 text-xs">{r.subscription_status}</td>
                  <td className="px-4 py-2.5 text-xs">{CNGN(r.subscription_value, r.currency)}</td>
                  <td className="px-4 py-2.5 text-xs">{(r.expires_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.invoice_status === "CONFIGURATION_INCOMPLETE" ? "Incomplete" : r.invoice_status || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.onboarding_complete ? "Complete" : "Pending"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="commercial-detail">
          <DialogHeader><DialogTitle>{detail?.organisation}</DialogTitle>
            <DialogDescription className="sr-only">Read-only commercial detail for this organisation.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="text-sm space-y-1.5">
              {[["Registration date", detail.registered_at], ["Registration number", detail.registration_number],
                ["Admin", `${detail.contact_name || "—"} (${detail.contact_email || "—"})`],
                ["Email verified", detail.email_verified ? "Yes" : "No"], ["Plan", detail.plan],
                ["Seats", `${detail.active_named_users}/${detail.contracted_named_users}`],
                ["Subscription value", CNGN(detail.subscription_value, detail.currency)],
                ["Commercial state", detail.commercial_state], ["Payment status", detail.legacy_migrated ? "Migrated account" : detail.payment_status],
                ["Subscription status", detail.subscription_status], ["Provider reference", detail.provider_transaction_reference || "—"],
                ["Invoice", `${detail.invoice_number || "—"} (${detail.invoice_status || "—"})`],
                ["Onboarding", detail.onboarding_complete ? "Complete" : "Pending"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-right">{v ?? "—"}</span></div>
              ))}
              <p className="text-xs text-muted-foreground pt-2">Read-only. No manual payment override in this view.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Orgs() {
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subscription_status: "Trial" });
  const load = () => api.get("/admin/organizations").then(({ data }) => setOrgs(data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/organizations", form); toast.success("Organisation created — admin invited by email"); setOpen(false); setForm({ subscription_status: "Trial" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setSub = async (id, status) => {
    try { await api.post(`/admin/organizations/${id}/subscription`, { subscription_status: status }); toast.success("Subscription updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const activate = async (id, plan) => {
    try { await api.post(`/admin/organizations/${id}/activate`, { plan }); toast.success("Organisation activated — admin notified"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const decline = async (id) => {
    try { await api.post(`/admin/organizations/${id}/decline`); toast.success("Registration declined"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pending = orgs.filter((o) => o.subscription_status === "Pending");
  return (
    <div>
      {pending.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden mb-6" data-testid="pending-orgs">
          <div className="px-5 py-3 border-b bg-amber-50/50"><h3 className="font-medium text-amber-800">Pending organisations ({pending.length})</h3></div>
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              <th className="px-5 py-2.5">Organisation</th><th className="px-5 py-2.5">Primary contact</th>
              <th className="px-5 py-2.5">Email</th><th className="px-5 py-2.5">Industry</th>
              <th className="px-5 py-2.5">Plan</th><th className="px-5 py-2.5">Registered</th>
              <th className="px-5 py-2.5 text-right">Action</th>
            </tr></thead>
            <tbody>
              {pending.map((o) => (
                <tr key={o.id} data-testid={`pending-row-${o.id}`} className="border-b last:border-0">
                  <td className="px-5 py-3 font-medium">{o.legal_name}</td>
                  <td className="px-5 py-3">{o.contact_name}</td>
                  <td className="px-5 py-3 text-xs">{o.contact_email}</td>
                  <td className="px-5 py-3 text-xs">{o.industry || "—"}</td>
                  <td className="px-5 py-3">{o.plan === "Monthly" ? "Monthly" : "Annual"}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" data-testid={`activate-${o.id}`} onClick={() => activate(o.id, o.plan === "Monthly" ? "Active Monthly" : "Active Annual")}>Activate</Button>
                    <Button size="sm" variant="outline" data-testid={`decline-${o.id}`} onClick={() => decline(o.id)}>Decline</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end mb-4"><Button data-testid="create-org-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Create organisation</Button></div>
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm epn-table">
          <thead className="bg-slate-50 border-b"><tr className="text-left">
            <th className="px-5 py-2.5">Organisation</th><th className="px-5 py-2.5">Plants</th>
            <th className="px-5 py-2.5">Products</th><th className="px-5 py-2.5">EPDs</th>
            <th className="px-5 py-2.5">Users</th><th className="px-5 py-2.5">Subscription</th>
          </tr></thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-b last:border-0">
                <td className="px-5 py-3 font-medium">{o.legal_name}{o.is_demo && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}</td>
                <td className="px-5 py-3">{o.plants}</td><td className="px-5 py-3">{o.products}</td>
                <td className="px-5 py-3">{o.epds}</td><td className="px-5 py-3">{o.users}</td>
                <td className="px-5 py-3">
                  <Select value={o.subscription_status} onValueChange={(v) => setSub(o.id, v)}>
                    <SelectTrigger data-testid={`sub-${o.id}`} className="w-40 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>{SUB.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 text-xs text-muted-foreground">Enterprise pricing: ₦2,500,000/month or ₦25,000,000/year (Save ₦5,000,000 annually). Commercial activation is handled manually — no automated payment processing.</div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create organisation (tenant)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Legal name *</Label><Input data-testid="org-legal-name" value={form.legal_name || ""} onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Admin full name *</Label><Input data-testid="org-admin-name" value={form.admin_name || ""} onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Admin email *</Label><Input data-testid="org-admin-email" type="email" value={form.admin_email || ""} onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Subscription</Label>
              <Select value={form.subscription_status} onValueChange={(v) => setForm((f) => ({ ...f, subscription_status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SUB.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="org-create-submit" onClick={create} disabled={!form.legal_name || !form.admin_email || !form.admin_name}>Create &amp; invite admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Factors() {
  const [factors, setFactors] = useState([]);
  const load = () => api.get("/factors").then(({ data }) => setFactors(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/factors/${id}/status`, { status }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <table className="w-full text-sm epn-table">
        <thead className="bg-slate-50 border-b"><tr className="text-left">
          <th className="px-4 py-2.5">Material / process</th><th className="px-4 py-2.5">GWP</th>
          <th className="px-4 py-2.5">Unit</th><th className="px-4 py-2.5">Source</th>
          <th className="px-4 py-2.5">Year</th><th className="px-4 py-2.5">Status</th>
          <th className="px-4 py-2.5 text-right">Set status</th>
        </tr></thead>
        <tbody>
          {factors.map((f) => (
            <tr key={f.id} data-testid={`factor-row-${f.id}`} className="border-b last:border-0">
              <td className="px-4 py-2.5 font-medium">{f.material}{f.demo_only && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO ONLY</span>}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{f.indicators?.["GWP-total"]}</td>
              <td className="px-4 py-2.5 text-xs">{f.unit}</td>
              <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[220px] truncate" title={f.source}>{f.source}</td>
              <td className="px-4 py-2.5 text-xs">{f.reference_year}</td>
              <td className="px-4 py-2.5"><span className={`text-xs rounded border px-2 py-0.5 ${f.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : f.status === "deprecated" ? "bg-slate-100 text-slate-500" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{f.status}</span></td>
              <td className="px-4 py-2.5 text-right">
                <Select value={f.status} onValueChange={(v) => setStatus(f.id, v)}>
                  <SelectTrigger data-testid={`factor-status-${f.id}`} className="w-32 h-7 ml-auto text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="deprecated">Deprecated</SelectItem>
                  </SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-4 text-xs text-muted-foreground border-t">Only <b>Approved</b>, non-demo factors may back an issued EPD. Demo factors are permitted for draft only and block final issuance.</div>
    </div>
  );
}

function Standards() {
  const [std, setStd] = useState({});
  useEffect(() => { api.get("/admin/standards").then(({ data }) => setStd(data)); }, []);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {Object.values(std).map((s) => (
        <div key={s.key} className="bg-white border rounded-lg p-5">
          <h3 className="font-medium">{s.name}</h3>
          <dl className="mt-3 text-sm space-y-1.5">
            <div className="flex justify-between"><dt className="text-muted-foreground">Standards</dt><dd className="text-right max-w-[60%]">{(s.standards || []).join(", ")}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">PCR</dt><dd className="text-right max-w-[60%]">{s.pcr}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">PCR version</dt><dd>{s.pcr_version}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Declared unit</dt><dd>{s.declared_unit}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Modules</dt><dd>{(s.required_modules || []).join(", ")}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Geography</dt><dd>{s.geography}</dd></div>
          </dl>
        </div>
      ))}
    </div>
  );
}

function Issuance() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/issuance").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <table className="w-full text-sm epn-table">
        <thead className="bg-slate-50 border-b"><tr className="text-left">
          <th className="px-5 py-2.5">Organisation</th><th className="px-5 py-2.5">Project</th>
          <th className="px-5 py-2.5">EPD ID</th><th className="px-5 py-2.5">Verified</th>
          <th className="px-5 py-2.5">Status</th><th className="px-5 py-2.5">Issue date</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="px-5 py-3">{r.organisation}</td><td className="px-5 py-3">{r.project}</td>
              <td className="px-5 py-3 font-mono text-xs">{r.epd_public_id || "—"}</td>
              <td className="px-5 py-3">{r.verified ? "✓" : "—"}</td>
              <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-5 py-3 text-xs">{r.issue_date || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-muted-foreground">No EPDs awaiting issuance.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Audit() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setEvents(data)); }, []);
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <table className="w-full text-sm epn-table">
        <thead className="bg-slate-50 border-b"><tr className="text-left">
          <th className="px-5 py-2.5">Time</th><th className="px-5 py-2.5">User</th>
          <th className="px-5 py-2.5">Action</th><th className="px-5 py-2.5">Object</th>
          <th className="px-5 py-2.5">Transition</th>
        </tr></thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} className="border-b last:border-0">
              <td className="px-5 py-2.5 text-xs text-slate-500">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</td>
              <td className="px-5 py-2.5 text-xs">{e.user_email || "system"}</td>
              <td className="px-5 py-2.5 font-mono text-xs">{e.action}</td>
              <td className="px-5 py-2.5 text-xs text-slate-600">{e.object_type}</td>
              <td className="px-5 py-2.5 text-xs">{e.prev_state && e.new_state ? `${e.prev_state} → ${e.new_state}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Methodology() {
  const [m, setM] = useState(null);
  const [cm, setCm] = useState(null);
  const [audit, setAudit] = useState(null);
  const [cat, setCat] = useState("cement");
  useEffect(() => { api.get("/admin/methodology").then(({ data }) => setM(data)); api.get("/admin/factor-audit").then(({ data }) => setAudit(data)); }, []);
  useEffect(() => { api.get(`/admin/compliance/${cat}`).then(({ data }) => setCm(data)); }, [cat]);
  if (!m) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const sec = m[cat];
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium">{m.title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{m.principle}</p>
        <p className="text-sm mt-3"><b>System boundary:</b> {m.system_boundary}</p>
        <p className="text-sm mt-1"><b>Profile:</b> {m.profile}</p>
        <div className="mt-4 flex gap-2">
          {["cement", "concrete"].map((c) => (
            <button key={c} data-testid={`method-cat-${c}`} onClick={() => setCat(c)} className={`text-xs rounded-full border px-3 py-1 ${cat === c ? "bg-emerald-50 border-emerald-300 text-emerald-800" : ""}`}>{c === "cement" ? "Cement" : "Concrete"}</button>
          ))}
        </div>
        <dl className="mt-3 text-sm space-y-2">
          {["A1", "A2", "A3", "allocation"].map((k) => (
            <div key={k}><dt className="font-medium">{k.toUpperCase()}</dt><dd className="text-slate-600">{sec[k]}</dd></div>
          ))}
        </dl>
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
          <b>Double-counting control:</b> {m.double_counting_control}
        </div>
        <div className="mt-3 text-xs text-slate-600"><b>Limitations:</b>
          <ul className="list-disc ml-5 mt-1">{m.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
        </div>
      </div>

      {cm && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b"><h3 className="font-medium">PCR / Standard compliance — {cm.profile}</h3></div>
          <table className="w-full text-sm"><thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
            <th className="px-4 py-2">Requirement</th><th className="px-4 py-2">Source</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Status</th>
          </tr></thead><tbody>
            {cm.rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-4 py-2">{r.requirement}</td><td className="px-4 py-2 text-xs text-slate-500">{r.source}</td>
                <td className="px-4 py-2 text-xs">{r.type}</td>
                <td className="px-4 py-2"><span className={`text-xs rounded border px-2 py-0.5 ${r.status.startsWith("PASS") ? "bg-emerald-50 border-emerald-200 text-emerald-700" : r.status.startsWith("NOT_IMPLEMENTED") || r.status.startsWith("FAIL") ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      {audit && (
        <div className="bg-white border rounded-lg p-5">
          <h3 className="font-medium mb-2">Factor provenance audit</h3>
          <div className="flex gap-4 text-xs mb-3">
            <span className="text-emerald-700">Approved: {audit.counts.approved}</span>
            <span className="text-amber-700">Draft: {audit.counts.draft}</span>
            <span className="text-slate-500">Deprecated: {audit.counts.deprecated}</span>
            <span className="text-amber-700">Demo-only: {audit.counts.demo_only}</span>
          </div>
          <p className="text-xs text-muted-foreground">Only Approved, non-demo factors with exact provenance may back an issued EPD. Factors without an exact, reproducible citation are held as Draft.</p>
        </div>
      )}
    </div>
  );
}

function DatasetMappings() {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => {
    api.get("/admin/dataset-mappings").then(({ data }) => setRows(data));
    api.get("/admin/lci-providers").then(({ data }) => setProviders(data));
    api.get("/admin/dataset-catalogue").then(({ data }) => setCatalogue(data));
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.post(`/admin/dataset-mappings/${id}/status`, { status }); load(); };
  const cats = ["", "cement_constituent", "mineral", "scm", "aggregate", "transport", "water", "energy", "process", "admixture"];
  const catFiltered = catalogue.filter((c) => !filter || c.category === filter);
  const repColor = (r) => ({ A: "text-emerald-700", B: "text-emerald-700", C: "text-amber-700", D: "text-orange-700", E: "text-red-700" }[r] || "text-slate-500");
  return (
    <div className="space-y-5">
      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-medium mb-2">Background LCI providers</h3>
        <div className="flex flex-wrap gap-3 text-xs">
          {providers.map((p) => (
            <span key={p.provider} data-testid={`lci-provider-${p.provider}`} className={`rounded border px-3 py-1 ${p.available ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
              {p.provider}{p.system_model ? ` · ${p.system_model}` : ""} — {p.available ? "available" : "not connected"}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Open datasets (ÖKOBAUDAT) are CANDIDATE until technically reviewed and verifier-accepted. Licensed datasets (ecoinvent EN15804) connect via server-side credentials. Issuance stays blocked until a dataset is Production Approved — no proprietary data is bundled.</p>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden" data-testid="dataset-catalogue">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium">Controlled dataset catalogue</h3>
          <select data-testid="catalogue-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded border border-input bg-background px-2 text-xs">
            {cats.map((c) => <option key={c} value={c}>{c ? c.replace(/_/g, " ") : "All categories"}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm"><thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
            <th className="px-4 py-2">Material</th><th className="px-4 py-2">Dataset</th><th className="px-4 py-2">Provider</th>
            <th className="px-4 py-2">Geo</th><th className="px-4 py-2">Fit</th><th className="px-4 py-2">Licence</th>
            <th className="px-4 py-2">Dataset status</th><th className="px-4 py-2">Qualification</th>
            <th className="px-4 py-2">Version</th><th className="px-4 py-2">Proxy</th><th className="px-4 py-2">Valid until</th>
          </tr></thead><tbody>
            {catFiltered.map((c) => (
              <tr key={c.id} data-testid={`catalogue-row-${c.material_key}`} className="border-b last:border-0">
                <td className="px-4 py-2 font-medium">{c.material_key}</td>
                <td className="px-4 py-2 text-xs">{c.dataset_name || <span className="text-red-600">{c.gap || "unmapped"}</span>}{c.dataset_uuid && <div className="text-[10px] text-slate-400 font-mono">{c.dataset_uuid}</div>}</td>
                <td className="px-4 py-2 text-xs">{c.provider}{c.source_database ? ` · ${c.source_database}` : ""}</td>
                <td className="px-4 py-2 text-xs">{c.geography || "—"}</td>
                <td className={`px-4 py-2 text-xs font-medium ${repColor(c.representativeness)}`}>{c.representativeness || "—"}</td>
                <td className="px-4 py-2 text-xs">{c.licence?.name ? (c.licence.commercial_use ? "OK (commercial)" : "restricted") : "—"}</td>
                <td className="px-4 py-2" data-testid={`catalogue-dsstatus-${c.material_key}`}><span className={`text-xs rounded border px-2 py-0.5 ${c.dataset_status === "APPROVED" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : c.dataset_status === "DEMO" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>{c.dataset_status || c.status}</span></td>
                <td className="px-4 py-2 text-xs" data-testid={`catalogue-qualstate-${c.material_key}`}>{c.qualification_state || "—"}{c.qualification_limitations && <div className="text-[10px] text-orange-600">{c.qualification_limitations}</div>}</td>
                <td className="px-4 py-2 text-xs font-mono">{c.dataset_version || "—"}</td>
                <td className="px-4 py-2 text-xs">{c.is_proxy ? <span className="text-orange-600" title={c.proxy_reason || ""}>proxy</span> : "—"}</td>
                <td className="px-4 py-2 text-xs">{c.qualification_review_expiry || "—"}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <p className="px-5 py-3 text-xs text-muted-foreground border-t">Representativeness — A: plant-specific · B: Nigeria · C: regional · D: foreign geography · E: proxy. Class D/E requires explicit technical review before Production Approval.</p>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b"><h3 className="font-medium">Dataset mappings (activity → background dataset)</h3></div>
        {rows.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No mappings yet. Mappings must be APPROVED before they can back a commercially issued EPD.</p> : (
          <table className="w-full text-sm"><thead className="bg-slate-50 border-b"><tr className="text-left text-xs text-slate-500">
            <th className="px-4 py-2">Material</th><th className="px-4 py-2">Provider</th><th className="px-4 py-2">Dataset ID</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
          </tr></thead><tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="px-4 py-2">{r.material_key}</td><td className="px-4 py-2">{r.provider}</td>
                <td className="px-4 py-2 text-xs">{r.provider_dataset_id}</td>
                <td className="px-4 py-2"><span className="text-xs rounded border px-2 py-0.5">{r.status}</span></td>
                <td className="px-4 py-2 text-right">{r.status !== "approved" && <Button size="sm" variant="outline" data-testid={`mapping-approve-${r.id}`} onClick={() => setStatus(r.id, "approved")}>Approve</Button>}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <div>
      <PageHeader title="Administration" subtitle="Platform administration — tenants, factors, standards, issuance and audit." />
      <Tabs defaultValue="orgs">
        <TabsList>
          <TabsTrigger value="orgs" data-testid="admin-tab-orgs"><Building2 className="h-4 w-4 mr-1.5" /> Organisations</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="admin-tab-commercial"><CreditCard className="h-4 w-4 mr-1.5" /> Commercial</TabsTrigger>
          <TabsTrigger value="factors" data-testid="admin-tab-factors"><Beaker className="h-4 w-4 mr-1.5" /> Factors</TabsTrigger>
          <TabsTrigger value="standards" data-testid="admin-tab-standards"><BookOpen className="h-4 w-4 mr-1.5" /> Standards / PCR</TabsTrigger>
          <TabsTrigger value="method" data-testid="admin-tab-method"><FlaskConical className="h-4 w-4 mr-1.5" /> Methodology</TabsTrigger>
          <TabsTrigger value="mappings" data-testid="admin-tab-mappings"><Beaker className="h-4 w-4 mr-1.5" /> Datasets</TabsTrigger>
          <TabsTrigger value="issuance" data-testid="admin-tab-issuance"><BadgeCheck className="h-4 w-4 mr-1.5" /> Issuance</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><ScrollText className="h-4 w-4 mr-1.5" /> Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="orgs" className="mt-5"><Orgs /></TabsContent>
        <TabsContent value="commercial" className="mt-5"><Commercial /></TabsContent>
        <TabsContent value="factors" className="mt-5"><Factors /></TabsContent>
        <TabsContent value="standards" className="mt-5"><Standards /></TabsContent>
        <TabsContent value="method" className="mt-5"><Methodology /></TabsContent>
        <TabsContent value="mappings" className="mt-5"><DatasetMappings /></TabsContent>
        <TabsContent value="issuance" className="mt-5"><Issuance /></TabsContent>
        <TabsContent value="audit" className="mt-5"><Audit /></TabsContent>
      </Tabs>
    </div>
  );
}
